import { useState, useRef } from "react";
import toast from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import {
    ErrorMessage,
    ErrorMessageWrapper
} from "../../../components/messages";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";
import { BasicToast } from "../../../components/toasts";
import useClientPreferences from "../hooks/useClientPreferences";
import useClientServiceTerms from "../hooks/useClientServiceTerms";
import {
    DateInput,
    FormCheck,
    FormSelect,
    FormTextArea,
    NumberInput
} from "./../../../components/forms/";
import GenericAdviserSelect from "./../../../components/forms/selects/GenericAdviserSelect";
import CenterFormColumnComponent from "./CenterFormColumnComponent";
import PortfolioBreakdown from "./PortfolioBreakdown";
import ServiceAgreementProofs from "./ServiceAgreementProofs";
import { useAuth } from "react-oidc-context";
import _ from "lodash";
import ClientServiceLevelAgreementStatusSelect from "../../../components/forms/selects/ClientServiceLevelAgreementSelect";
import useClientServiceLevelAgreementStatuses from "../hooks/useClientServiceLevelAgreementStatuses";

const months = [
    { label: "January", value: 1, maxDays: 31 },
    { label: "February", value: 2, maxDays: 28 },
    { label: "March", value: 3, maxDays: 31 },
    { label: "April", value: 4, maxDays: 30 },
    { label: "May", value: 5, maxDays: 31 },
    { label: "June", value: 6, maxDays: 30 },
    { label: "July", value: 7, maxDays: 31 },
    { label: "August", value: 8, maxDays: 31 },
    { label: "September", value: 9, maxDays: 30 },
    { label: "October", value: 10, maxDays: 31 },
    { label: "November", value: 11, maxDays: 30 },
    { label: "December", value: 12, maxDays: 31 },
];   
    
const excludedDates = [new Date(2024, 1, 29), new Date(2028, 1, 29), new Date(2032, 1, 29), new Date(2036, 1, 29), new Date(2040, 1, 29), new Date(2044, 1, 29)]; 

const ServiceAgreementDetails = ({ masterAccountId, loadingLines = 3, allowEditReviewDateRoles = ["sys_admin_access", "csla_admin"] }) => {

    const auth = useAuth();
    const { user : { profile: { roles } } } = auth;
    const statusControlRef = useRef(null);
    const termsControlRef = useRef(null);
    
    const {
        clientServiceTerms,
        isLoading,
        isError,
        error,
        reload,
        serviceLevelAgreements,
        updateClientServiceAgreement,
        createSLA,
    } = useClientServiceTerms(masterAccountId);

    const {fetchStatuses} = useClientServiceLevelAgreementStatuses();

    const { 
        servicePreferences,
        servicePreferencesError,
        servicePreferencesIsError,
        servicePreferencesIsErrorUpdating,
        servicePreferencesIsLoading,
        servicePreferencesIsUpdating,
        reloadServicePreferences,
        servicePreferencesErrorUpdateError,
        updateServicePreference
    } = useClientPreferences(masterAccountId);

    const handleRetry = (e) => {
        e.preventDefault();
        reload(e);
    };

    const onFieldChanged = (clientServiceAgreementId, property, value) =>
        updateClientServiceAgreement(
            clientServiceTerms.clientAgreementId,
            property,
            value
    );

    const onPreferencesFieldChanged = (property, value) => updateServicePreference(property, value);

    
    const onAnniversaryMonthChanged = (selection) => {
        if (clientServiceTerms.anniversaryDay > selection.maxDays) {
            onFieldChanged(
                clientServiceTerms.clientAgreementId,
                "anniversaryDay",
                selection.maxDays
            );
        }

        onFieldChanged(
            clientServiceTerms.clientAgreementId,
            "anniversaryMonth",
            selection.value
        );
    };

    const onStatusChanged = (newStatus) => {
        fetchStatuses(clientServiceTerms.clientAgreementId).then(
            (r) => {
                const originalStatus = clientServiceTerms?.clientServiceLevelAgreementStatus.id;
                const originalStatusObject = r.find(el => el?.value === originalStatus);
                
                // changing from operational
                if(originalStatusObject.data.selector === 1 && newStatus.data.selector !== 1){

                    openConfirmModal({
                        dialogTitle: "Updating Status",
                        message: "Updating the status from operational will stop future reviews from being created. New terms must be previewed, published and uploaded before reviews can be completed."
                    }).then(
                        (proceed) => {
                            if (proceed)
                                onFieldChanged(
                                    clientServiceTerms.clientAgreementId,
                                    "statusId",
                                    newStatus.value
                            )
                            else {
                                statusControlRef.current.setValue(originalStatusObject);
                            }
                        }
                    );

                    return;
                }
                else if(newStatus === 1){
                    var errors = [];

                    //do some validation
                    if(clientServiceTerms.initialReviewDate == null){
                        errors.push("Initial Review Date");
                    }
                    
                    if(errors.length > 0){

                        toast.custom((t) => <BasicToast 
                            control={t}
                            icon="fa-solid fa-user-plus" 
                            title="Cannot set terms to operational" 
                            message={errors.join(' ')}
                        />, { duration: 8000 });

                        return;

                    }

                }
                
                onFieldChanged(
                    clientServiceTerms.clientAgreementId,
                    "statusId",
                    newStatus.value
                );
            }
        )
                                 
    }
    
    const onCreatePressed = (e) => createSLA(e);

    const canEdit = () => {

        
        if(clientServiceTerms?.clientServiceLevelAgreementStatus?.selector === 0) return true;
        
        if(clientServiceTerms?.clientServiceLevelAgreementStatus?.selector === 1) return false;
        if(clientServiceTerms?.feeAgreements.some(x => x.isActive && x.signedDate == null && x.status > 1)) return false;
        return true;
    }

    const canEditReviewDate = () => {
        var isAllowedToEditReviewDate =  _.some(roles, (item) => allowEditReviewDateRoles.includes(item));

        return clientServiceTerms.clientServiceLevelAgreementStatus.selector === 1 && isAllowedToEditReviewDate;

    }

    if (error || isError === true) {
        return (
            <ErrorMessageWrapper margin="0px auto" maxWidth="500px">
                <ErrorMessage error={error} retryCallback={handleRetry} />
            </ErrorMessageWrapper>
        );
    }

    return (
        <div>
            {isLoading === true && <Skeleton count={loadingLines} />}
            {!isLoading && Object.keys(clientServiceTerms).length === 0 && (
                <div className="row mb-2">
                    <div className="col">
                        There is no active client agreements available. You can
                        click{" "}
                        <a href="#" onClick={onCreatePressed}>
                            here
                        </a>{" "}
                        to create one.
                    </div>
                </div>
            )}
            {!isLoading && Object.keys(clientServiceTerms).length > 0 && (
                <div>
                    <div className="row mb-4">
                        <div className="col">
                            <FormSelect
                                ref={termsControlRef}
                                label="Terms"
                                options={serviceLevelAgreements}
                                isDisabled={!canEdit()}
                                defaultValue={
                                    clientServiceTerms?.serviceLevelAgreementId
                                }
                                onBlur={(selection) => {
                                    if (
                                        clientServiceTerms.feeAgreements
                                            ?.length > 0
                                    ) {
                                        openConfirmModal({
                                            dialogTitle: "Updating service terms",
                                            message: "Updating the service terms will override some of the options on this page. Are you sure you want to continue?"
                                        })
                                        .then(
                                            (proceed) => {
                                                if (proceed) updateClientServiceAgreement(
                                                                clientServiceTerms.clientAgreementId,
                                                                "serviceLevelAgreementId",
                                                                selection.value
                                                            );
                                                else {
                                                    const originalTerms = clientServiceTerms?.serviceLevelAgreementId;
                                                    const originalTermsObject = serviceLevelAgreements.find(el => el?.value === originalTerms);

                                                    termsControlRef.current.setValue(originalTermsObject);

                                                }
                                            }
                                        )
                                    } else {
                                        onFieldChanged(
                                            clientServiceTerms.clientAgreementId,
                                            "serviceLevelAgreementId",
                                            selection.value
                                        );
                                    }
                                }}
                            />
                        </div>
                        <div className="col">
                            <FormSelect
                                label="Frequency"
                                defaultValue={
                                    clientServiceTerms?.reviewFrequency
                                }
                                options={[
                                    { label: "Annually", value: 12 },
                                    { label: "6 Monthly", value: 6 },
                                    { label: "3 Monthly", value: 3 },
                                    { label: "Biennially", value: 24 }
                                ]}
                                onBlur={(selection) => {
                                        onFieldChanged(
                                            clientServiceTerms.clientAgreementId,
                                            "reviewFrequency",
                                            selection.value
                                        );
                                }}
                                isDisabled={!canEdit()}
                            />
                        </div>
                        <div className="col-md-auto">
                            <FormCheck
                                type="checkbox"
                                label="Transaction Fees"
                                name="hasTransactionFees"
                                vertical={true}
                                labelFirst={true}
                                className="mt-3"
                                disabled={!canEdit()}
                                isChecked={
                                    clientServiceTerms.doTransactionFeesApply
                                }
                                onChange={() =>
                                    onFieldChanged(
                                        clientServiceTerms.clientAgreementId,
                                        "doTransactionFeesApply",
                                        !clientServiceTerms.doTransactionFeesApply
                                    )
                                }
                            />
                        </div>
                        <div className="col-md-auto">
                            <FormCheck
                                type="checkbox"
                                label="Drawdown Review"
                                name="isDrawdownReview"
                                vertical={true}
                                labelFirst={true}
                                className="mt-3"
                                isChecked={
                                    clientServiceTerms.incomeDrawdownOption
                                }
                                onChange={() =>
                                    onFieldChanged(
                                        clientServiceTerms.clientAgreementId,
                                        "incomeDrawdownOption",
                                        !clientServiceTerms.incomeDrawdownOption
                                    )
                                }
                            />
                        </div>
                        <div className="col-3">
                            <ClientServiceLevelAgreementStatusSelect 
                                ref={statusControlRef}
                                label="Status"
                                cslaId={clientServiceTerms?.clientAgreementId}
                                defaultValue={clientServiceTerms.statusId}
                                onChange={(selection) => onStatusChanged(selection)}
                            />
                        </div>
                        <div className="col-2">
                            <DateInput
                                label="Status Date"
                                disabled={!canEdit()}
                                value={clientServiceTerms.statusDate}
                                onBlur={(value) =>
                                    onFieldChanged(
                                        clientServiceTerms.clientAgreementId,
                                        "statusDate",
                                        value
                                    )
                                }
                            />
                        </div>
                    </div>
                    {!servicePreferencesIsError &&(
                        <div className="row mb-4">
                            <div className="col-3">
                                <GenericAdviserSelect
                                    label="Review Adviser"
                                    placeholder="Review Adviser"
                                    defaultValue={servicePreferences?.reviewAdviserId}
                                    onBlur={(values) =>
                                        onPreferencesFieldChanged(
                                            "reviewAdviserId",
                                            values.value
                                        )
                                    }
                                    isDisabled={!canEdit()} 
                                />
                            </div>
                            <div className="col-3">
                                <FormSelect
                                    label="Appointment Style"
                                    defaultValue={servicePreferences?.appointmentStyle}
                                    isDisabled={!canEdit()}
                                    options={[
                                        { label: "Home Meeting", value: 0 },
                                        { label: "Office Meeting", value: 1 },
                                        { label: "Phone Call", value: 2 },
                                        { label: "Video Meeting", value: 3 },
                                        {
                                            label: "No Appointment Required",
                                            value: 4,
                                        },
                                    ]}
                                    onBlur={(values) =>
                                        onPreferencesFieldChanged(
                                            "appointmentStyle",
                                            values.value
                                        )
                                    }
                                />
                            </div>
                            <div className="col-3">
                                <FormSelect
                                    label="Scheduling"
                                    defaultValue={servicePreferences?.appointmentSchedulingType}
                                    options={[
                                        { label: "Automated", value: 0 },
                                        { label: "Manual", value: 1 },
                                        {
                                            label: "No Scheduling Required",
                                            value: 2,
                                        },
                                    ]}
                                    onBlur={(values) =>
                                        onPreferencesFieldChanged(
                                            "appointmentSchedulingType",
                                            values.value
                                        )
                                    }
                                isDisabled={!canEdit()}
                                />
                            </div>
                            <div className="col-3">
                                <FormSelect
                                    label="Adviser Updates"
                                    isDisabled={!canEdit()}
                                    options={[
                                        { label: "No Updates", value: 0 },
                                        { label: "3 Monthly", value: 1 },
                                        { label: "6 Monthly", value: 2 },
                                    ]}
                                    defaultValue={
                                        clientServiceTerms.adviserUpdateFrequency
                                    }
                                    onBlur={(selection) =>
                                        onFieldChanged(
                                            clientServiceTerms.clientAgreementId,
                                            "adviserUpdateFrequency",
                                            selection.value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {servicePreferencesIsError && (
                        <div className="row mb-2">
                        <ErrorMessageWrapper margin="0px auto" maxWidth="500px">
                            <ErrorMessage error={servicePreferencesError} retryCallback={reloadServicePreferences} />
                        </ErrorMessageWrapper>
                        </div>
                    )}
                    <div className="row mb-4">
                        <div className="col-2"> 
                            <DateInput 
                                label="Initial Review Date" 
                                value={clientServiceTerms.initialReviewDate}
                                onBlur={(value) => {

                                    openConfirmModal({
                                        allowConfirmButton: false,
                                        dialogTitle: "Updating review date",
                                        message: "This is the date that the first review will take place and will be used to generate the first system generated review. Please ensure it has been set correctly."
                                    })
                                    .then(
                                         onFieldChanged( 
                                            clientServiceTerms.clientAgreementId, 
                                            "initialReviewDate",
                                            value 
                                        )
                                    )

                                   
                                }}
                                excludeDates={excludedDates} 
                                minDate={new Date()} 
                                isClearable
                                disabled={!canEdit() && !canEditReviewDate()}
                            /> 
                        </div> 
                        <div className="col-2">
                            <FormSelect
                                label="Anniversary"
                                options={months}
                                defaultValue={
                                    clientServiceTerms.anniversaryMonth
                                }
                                onBlur={(selection) =>
                                    onAnniversaryMonthChanged(selection)
                                }
                                isDisabled={!canEdit() && !canEditReviewDate()}
                            />
                        </div>
                        <CenterFormColumnComponent className="col-1">
                            <NumberInput
                                value={clientServiceTerms.anniversaryDay}
                                onBlur={(value) =>
                                    onFieldChanged(
                                        clientServiceTerms.clientAgreementId,
                                        "anniversaryDay",
                                        value
                                    )
                                }
                                suffix={""}
                                disabled={!canEdit() && !canEditReviewDate()}
                                isAllowed={(values) => {
                                    if (!values.value) return true;
                                    const { floatValue } = values;
                                    return (
                                        floatValue <=
                                        months.find(
                                            (x) =>
                                                x.value ==
                                                clientServiceTerms.anniversaryMonth
                                        ).maxDays
                                    );
                                }}
                                decimalScale={0}
                            />
                        </CenterFormColumnComponent>
                    </div>

                    <PortfolioBreakdown
                        masterAccountId={masterAccountId}
                        clientServiceAgreementId={
                            clientServiceTerms.clientAgreementId
                        }
                        feeBreakdowns={clientServiceTerms?.feeBreakdowns}
                        canEdit={canEdit}
                    />

                    <div className="row mb-4">
                        <div className="col">
                            <FormTextArea
                                label="Special Instructions"
                                value={clientServiceTerms.specialInstructions}
                                onBlur={(value) =>
                                    onFieldChanged(
                                        clientServiceTerms.clientAgreementId,
                                        "specialInstructions",
                                        value
                                    )
                                }
                                minRows={3}
                                maxRows={3}
                                disabled={!canEdit()}
                            />
                        </div>
                    </div>

                    <ServiceAgreementProofs
                        masterAccountId={masterAccountId}
                        clientServiceAgreementId={
                            clientServiceTerms.clientAgreementId
                        }
                        feeAgreements={clientServiceTerms.feeAgreements}
                        feeSignatories={clientServiceTerms.feeSignatories}
                        canEdit={canEdit}
                        isPreviewed={clientServiceTerms.termsHaveBeenPreviewed}
                    />
                </div>
            )}
        </div>
    );
};

export default ServiceAgreementDetails;
